import React from "react";
import { Calculator, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import HbA1cCalc from "../components/calculators/HbA1cCalc";
import BMICalc from "../components/calculators/BMICalc";
import ICRCalc from "../components/calculators/ICRCalc";
import ISFCalc from "../components/calculators/ISFCalc";
import FitnessandPromo from "../components/calculators/FitnessandPromo";
import { pagePath } from "../lib/routes";

export default function Calculators({ lang }) {
  return (
    <>
      <SEO
        lang={lang}
        page="calculators"
        title={
          lang === "et"
            ? "Tervisekalkulaatorid — HbA1c, KMI, ICR ja ISF | Tallinna Endokrinoloogia Kliinik"
            : lang === "ru"
            ? "Калькуляторы здоровья — HbA1c, ИМТ, ICR и ISF | Таллиннская клиника эндокринологии"
            : "Health calculators — HbA1c, BMI, ICR and ISF | Tallinn Endocrinology Clinic"
        }
        description={
          lang === "et"
            ? "Tasuta tervisekalkulaatorid: HbA1c teisendus, kehamassiindeks (KMI) ja vööümbermõõdu risk, ICR (insuliini-süsivesikute suhe) ja ISF (insuliini tundlikkuse tegur)."
            : lang === "ru"
            ? "Бесплатные калькуляторы: HbA1c, ИМТ, талия, ICR (соотношение инсулин/углеводы) и ISF (фактор чувствительности к инсулину)."
            : "Free health calculators: HbA1c conversion, BMI and waist risk, ICR (insulin-to-carb ratio) and ISF (insulin sensitivity factor)."
        }
      />

      <section className="container-page pt-12 pb-6" data-testid="calculators-page">
        <div className="chip mb-3">
          <Calculator className="h-3.5 w-3.5 inline mr-1" />
          {lang === "et" ? "KALKULAATORID" : lang === "ru" ? "КАЛЬКУЛЯТОРЫ" : "CALCULATORS"}
        </div>
        <h1 className="section-title">
          {lang === "et" ? "Tervisekalkulaatorid" : lang === "ru" ? "Калькуляторы здоровья" : "Health calculators"}
        </h1>
        <p className="text-surface-inkSoft mt-3 text-[15px] max-w-2xl">
          {lang === "et"
            ? "Hinda oma diabeedi kontrolli ja kehakaalu seisundit lihtsate kalkulaatoritega. Tulemused on informatiivsed — täpse diagnoosi ja ravi jaoks broneeri vastuvõtt."
            : lang === "ru"
            ? "Простые инструменты для оценки контроля диабета и веса. Результаты информативные — для диагноза обращайтесь к врачу."
            : "Simple tools to assess your diabetes control and weight. Informational only — book an appointment for a proper diagnosis."}
        </p>
      </section>

      <section className="container-page pb-10 space-y-8">
        <HbA1cCalc lang={lang} />
        <BMICalc lang={lang} />
        <ICRCalc lang={lang} />
        <ISFCalc lang={lang} />
      </section>

      <FitnessandPromo lang={lang} />

      <section className="container-page pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-sapphire-700 to-[#005a87] text-white p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <div className="text-[13px] uppercase tracking-wider opacity-80 mb-1">
              {lang === "et" ? "Vajad personaalset nõu?" : lang === "ru" ? "Нужна личная консультация?" : "Need personal advice?"}
            </div>
            <h3 className="text-2xl font-semibold">
              {lang === "et" ? "Broneeri vastuvõtt endokrinoloogi juurde" : lang === "ru" ? "Запишитесь на приём к эндокринологу" : "Book a consultation with our endocrinologist"}
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calc-book-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-white text-sapphire-700 px-5 py-3 font-medium text-[14px] hover:bg-sapphire-50 transition-colors"
            >
              {lang === "et" ? "Broneeri" : lang === "ru" ? "Записаться" : "Book"} <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to={pagePath("diseases", lang)}
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-white px-5 py-3 font-medium text-[14px] hover:bg-white/15 transition-colors"
            >
              {lang === "et" ? "Loe haiguste kohta" : lang === "ru" ? "Читать о заболеваниях" : "Read about diseases"}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
